import {
  createContext,
  useContext,
  useMemo,
  ReactNode,
} from 'react';

import { useAuth } from 'context/AuthContext';
import { useGetStaff } from 'hooks/useUsers';

/* =========================
   Types
========================= */

type UsersContextType = {
  staff: any[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

type UsersProviderProps = {
  children: ReactNode;
};

/* =========================
   Context
========================= */

const UsersContext = createContext<UsersContextType | undefined>(
  undefined
);

/* =========================
   Provider
========================= */

export function UsersProvider({ children }: UsersProviderProps) {
  const { user } = useAuth();

  const {
    data: staff = [],
    refetch,
    isLoading,
    isFetching,
  } = useGetStaff(user?.id, {
    enabled: Number.isFinite(user?.id),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 60_000,
    select: (res: any) => {
      const filtered =
        res?.data?.user_staff?.filter((item: any) => item.end_user !== null) || [];

      return filtered.sort((a: any, b: any) =>
        a.status.localeCompare(b.status)
      );
    },
  });

  const value = useMemo(
    () => ({ staff, isLoading, isFetching, refetch }),
    [staff, isLoading, isFetching, refetch]
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

/* =========================
   Hook
========================= */

export function useUsersContext() {
  const context = useContext(UsersContext);

  if (!context) {
    throw new Error(
      'useUsersContext must be used within a UsersProvider'
    );
  }

  return context;
}
